<template>
  <div class="scroll view">
    <div v-if="loadingConfirmed" style="align-self: center; margin-bottom: 1rem;">
      loading...
    </div>
    <owal-button style="align-self: center; margin-bottom: 1rem;" @click="lockAll" v-if="canLockTrainingData && loadingConfirmed === false">
      lock all
    </owal-button>
    <div id="confirmed">
      <div v-if="facesConfirmedFiltered && facesConfirmedFiltered.length" class="confirmed-list">
        <div
          v-for="confirmed of facesConfirmedFiltered"
          :key="confirmed.detection_id"
          class="pair"
          >
          <font-awesome-icon
            v-if="canLockTrainingData"
            :icon="confirmed.is_locked ? `lock` : `lock-open`"
            size="2x"
            @click="queueMatch(confirmed)"
            style="width: 32px; cursor: pointer; margin-right: .5rem;"
            />
          <detection
            :profile="confirmed"
            :associatedImageURL="associatedImageURL(confirmed)"
            :baseImageURL="confirmedImageURL(confirmed.detection_id)"
            :imageTitle="imageTitle(confirmed)"
            @click="removeConfirm(confirmed.frontend_face_uuid, confirmed.detection_id, confirmed.face_match_uuid)"
            :showRemove="!confirmed.is_locked"
            :associatedImageStyle="associatedImageStyle(confirmed.evaluatorData)"
            />
        </div>
      </div>
      <div v-if="facesConfirmed && !facesConfirmed.length">
        No results.
      </div>
    </div>
  </div>
</template>

<script>
import { facesconfirmed, facematchconfirm, imageForDetectionID, urlForFrontendFaceId, updateLockedTrainingFor } from '@/libraries/api/faces'
import Detection from '@/components/shared/BaseDetection'
import { mapGetters } from 'vuex'
import moment from 'moment'
import { OwalButton } from '@/libraries/owal-ui'

export default {
  name: 'FacesConfirmed',
  components: {
    Detection,
    OwalButton,
  },
  props: {
    faceId: {
      type: String,
      default: undefined,
    },
  },
  data () {
    return {
      facesConfirmed: undefined,
      seenIds: [],
      highestIndex: 0,
      loadingConfirmed: false,
      timeout: undefined,
      tagsForLocking: [],
    }
  },
  computed: {
    ...mapGetters({
      currentDomainUUID: 'currentDomainUUID',
      currentModel: 'trainingTool/currentModel',
      canPerformOperationForCurrentService: 'entitlements/canPerformOperationForCurrentService',
    }),
    canLockTrainingData () {
      return this.canPerformOperationForCurrentService('lock_training_data')
    },
    facesConfirmedFiltered () {
      if (this.facesConfirmed) {
        const sorted = this.facesConfirmed.slice().sort((matchA, matchB) => {
          if (matchA.evaluatorData && matchB.evaluatorData) {
            return matchA.evaluatorData.true_prob - matchB.evaluatorData.true_prob
          } else if (!matchA.evaluatorData) {
            return 1
          } else if (!matchB.evaluatorData) {
            return -1
          } else return 0
        })
        return sorted
      } else {
        return this.facesConfirmed
      }
    },
  },
  methods: {
    queueMatchesForLocking (uuids) {
      clearTimeout(this.timeout)
      this.tagsForLocking = this.tagsForLocking.concat(uuids)
      this.timeout = setTimeout(function () {
        console.log(this.tagsForLocking)
        updateLockedTrainingFor(this.tagsForLocking, this.currentDomainUUID).then(res => {
          this.updateTagsUI()
        }).catch(err => console.log(err))
      }.bind(this), 1000)
    },
    lockAll () {
      const locked = this.facesConfirmed.map(confirmed => {
        return {
          face_match_uuid: confirmed.face_match_uuid,
          is_locked: true,
        }
      })
      this.queueMatchesForLocking(locked)
    },
    updateTagsUI () {
      this.tagsForLocking.forEach(function (match) {
        const index = this.facesConfirmed.map(match => match.face_match_uuid).indexOf(match.face_match_uuid)
        if (index >= 0) {
          this.facesConfirmed[index].is_locked = match.is_locked
        }
      }.bind(this))
      this.tagsForLocking = []
    },
    queueMatch (match) {
      this.queueMatchesForLocking([{ face_match_uuid: match.face_match_uuid, is_locked: !match.is_locked }])
    },
    confirmedImageURL (detectionId) {
      return imageForDetectionID(detectionId)
    },
    imageTitle (match) {
      let title = `tagged by: ${match.tagged_by} on ${moment.utc(match.confirmed_at).local().format('DD-MM-YYYY HH-mm-ss')}`
      if (match.evaluatorData) {
        const evalString = `pred prob: ${match.evaluatorData.pred_prob}\n` +
        `true prob: ${match.evaluatorData.true_prob}`
        return `${title}\n` + evalString
      } else {
        return title
      }
    },
    associatedImageStyle (evaluatorData) {
      if (evaluatorData) {
        const incorrect = !evaluatorData.success || evaluatorData.pred_frontend_uuid === 1
        return incorrect ? 'border: 4px solid rgba(202, 0, 79, 0.9)' : 'border: 4px solid rgba(34, 139, 34, 0.5)'
      }
    },
    associatedImageURL (confirmed) {
      if (!confirmed.evaluatorData) {
        return undefined
      } else {
        return urlForFrontendFaceId(confirmed.evaluatorData.pred_frontend_uuid)
      }
    },
    removeConfirm (frontendFaceUUID, detectionId, faceMatchID) {
      facematchconfirm(detectionId, frontendFaceUUID, false, this.currentDomainUUID, faceMatchID).then(res => res.data).then(data => {
        if (!data.ok) {
          console.log(data.err)
          return
        }
        const newFacesConfirmed = this.facesConfirmed
        newFacesConfirmed.splice(newFacesConfirmed.findIndex(x => x.detection_id === detectionId), 1)
        this.facesConfirmed = newFacesConfirmed
      }).catch(err => {
        console.log(err)
      })
    },
  },
  mounted () {
    // NOTE: mounted () runs whenever the parent profile changes, so we don't have to watch it
    const modelKey = this.currentModel.key || undefined
    this.loadingConfirmed = true
    facesconfirmed(this.currentDomainUUID, 0, 100000, this.faceId, modelKey).then(res => res.data).then(function (data) {
      if (!data.ok) {
        console.log(data.err)
        return
      }
      this.facesConfirmed = data.faces_confirmed
      this.loadingConfirmed = false
    }.bind(this)).catch((err) => {
      console.log(err)
      this.loadingConfirmed = false
    })
  },
}
</script>

<style scoped>

.scroll.view {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: auto;
  padding-bottom: 3rem;
}

@media only screen and (min-width: 768px) {
  .confirmed-list {
    justify-content: space-between;
  }
}

/* Mobile */
@media only screen and (max-width: 768px) {
  .confirmed-list {
    justify-content: center;
  }
}

#confirmed {
  height: 100%;
}
.confirmed-list {
  display: flex;
  flex-wrap: wrap;
}

.pair {
  border-radius: 10px;
  height: 130px;
  margin: 50px 10px;
  padding: 10px;
  display: flex;
  flex-direction: row;
  align-items: center;
}

.highlighted.pair {
  background: #f5f5f5;
}

.pair .original {
  z-index: 1;
}

.pair .detection {
  transform: scale(0.9, 0.9);
  z-index: 2;
}

</style>
